import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { z } from 'zod'
import { flattenPayloadFormat, unescapeDotsInKey } from './utils'

export type ApiRequestPayloadFormValues = Record<string, string | number | boolean | undefined>

// Map payload format types to component types and validation
const getFieldConfigForType = (
  key: string,
  type: string,
): GenericFieldConfig<ApiRequestPayloadFormValues>[0] => {
  const baseName = key as keyof ApiRequestPayloadFormValues
  // Display label with unescaped dots for better readability
  const displayLabel = unescapeDotsInKey(key)

  switch (type) {
    case 'boolean':
      return {
        name: baseName,
        componentType: 'checkbox',
        zodValidation: z.boolean(),
        inputProps: {
          label: displayLabel,
          'data-testid': `input-payload-${key}`,
        },
      }
    case 'number':
      return {
        name: baseName,
        componentType: 'inputNumber',
        zodValidation: z.number(),
        inputProps: {
          label: displayLabel,
          placeholder: 'Enter number',
          type: 'number',
          'data-testid': `input-payload-${key}`,
        },
      }
    case 'list':
      return {
        name: baseName,
        type: 'custom',
        component: ({ onChange, value }) => (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-s-600 text-secondary">
                {displayLabel} <span className="text-s-400 italic text-tertiary">(Array)</span>
              </span>
            </div>
            <CodeEditor
              language="json"
              value={typeof value === 'string' ? value : '[]'}
              onChange={onChange}
              className="border border-st2 rounded-md overflow-auto"
              data-testid={`code-editor-payload-${key}`}
            />
          </div>
        ),
        eventDataExtractor: (data) => data,
        zodValidation: z
          .string()
          .min(1, 'List value is required')
          .refine(
            (val) => {
              try {
                const parsed = JSON.parse(val)
                return Array.isArray(parsed)
              } catch {
                return false
              }
            },
            {
              message: 'Must be a valid JSON array (e.g., ["item1", "item2"])',
            },
          ),
      }
    case 'json':
      return {
        name: baseName,
        type: 'custom',
        component: ({ onChange, value }) => (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-s-600 text-secondary">
                {displayLabel} <span className="text-s-400 italic text-tertiary">(JSON)</span>
              </span>
            </div>
            <CodeEditor
              language="json"
              value={typeof value === 'string' ? value : '{}'}
              onChange={onChange}
              className="border border-st2 rounded-md overflow-auto"
              data-testid={`code-editor-payload-${key}`}
            />
          </div>
        ),
        eventDataExtractor: (data) => data,
        zodValidation: z
          .string()
          .min(1, 'JSON value is required')
          .refine(
            (val) => {
              try {
                const parsed = JSON.parse(val)
                return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
              } catch {
                return false
              }
            },
            { message: 'Must be a valid JSON object (e.g., {"key": "value"})' },
          ),
      }
    default:
      return {
        name: baseName,
        componentType: 'input',
        zodValidation: z.string().min(1, 'Value is required'),
        inputProps: {
          label: displayLabel,
          placeholder: 'Enter value',
          'data-testid': `input-payload-${key}`,
        },
      }
  }
}

// Create field configuration based on payload keys and their types
export const createApiRequestPayloadFields = (
  payloadFormat: Record<string, string | Record<string, unknown>>,
): GenericFieldConfig<ApiRequestPayloadFormValues> => {
  // First, flatten the nested structure
  const flattenedFormat = flattenPayloadFormat(payloadFormat)

  // Then create fields for each flattened path
  return Object.entries(flattenedFormat).map(([key, type]) => getFieldConfigForType(key, type))
}
